// A list or table with its bar, and every state around it decided here, so
// every list shows them the same way:
//   - the first read is out (`count` null): the bar, then the list, which
//     draws its own skeleton;
//   - the read failed with nothing to show: a Notice (`errorTitle` over the
//     error) with Retry, no bar (a count would claim to know what isn't
//     known); under a search, the bar stays and the error sits in it;
//   - nothing at all: the empty state alone, with the way to add the first
//     (a "0 things" bar over nothing reads as a broken table);
//   - nothing for the search or filter (`filtered`): the bar, so the search
//     can be cleared, then a line saying so;
//   - rows: the bar over the list, and a failed refresh beside the count.
// `bar` is what the bar holds (search, filters, its Count); `barEnd` sits at
// its end (columns, show all). `empty` is { title, note, action }: the note
// says what to do; `action` is only for a way that isn't already the page's
// main action at the top right (never a second Add button).
import Notice, { ErrorText } from "./Notice.jsx";
import Button from "./Button.jsx";
import EmptyState from "./EmptyState.jsx";
import TableBar from "./TableBar.jsx";

export default function Listing({
    count,
    error = null,
    errorTitle = null,
    onRetry = null,
    retrying = false,
    filtered = false,
    bar = null,
    barEnd = null,
    empty,
    noMatch = "Nothing matches. Clear the search to see everything.",
    className = "",
    children,
}) {
    if (error && !count && !filtered) {
        return (
            <div className={className} data-listing="failed">
                <Notice tone="error" title={errorTitle} action={onRetry ? <Button size="sm" onClick={onRetry} disabled={retrying}>Retry</Button> : null}>
                    {error}
                </Notice>
            </div>
        );
    }
    if (count === 0 && !filtered) {
        return (
            <div className={className} data-listing="empty">
                <EmptyState className="max-w-[640px] px-[16px] py-[16px]" title={empty.title} note={empty.note} action={empty.action} />
            </div>
        );
    }
    const showBar = bar || barEnd || error;
    return (
        <div className={`flex min-w-0 flex-col ${className}`} data-listing={count == null ? "loading" : count ? "rows" : "no-match"}>
            {showBar ? (
                <TableBar end={error || barEnd ? <>{error ? <ErrorText>{error}</ErrorText> : null}{barEnd}</> : null}>
                    {bar}
                </TableBar>
            ) : null}
            {count === 0 ? (
                <p className="m-0 px-[6px] py-[14px] text-[13px] leading-[1.45] text-[color:var(--muted)]">{noMatch}</p>
            ) : children}
        </div>
    );
}
